import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { SEO } from '../components/SEO';

const API = `${window.location.origin}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Sample blog posts
const samplePosts = [
  {
    post_id: 'sample1',
    title: 'The Art of Luxury Event Design: 2024 Trends',
    excerpt: 'Discover the latest trends shaping luxury event design this year, from sustainable elegance to immersive experiences.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1767050190883-29d644fa5b99?q=85&w=800&auto=format&fit=crop',
    category: 'Design Trends',
    author_name: 'Sarah Mitchell',
    created_at: new Date().toISOString()
  },
  {
    post_id: 'sample2',
    title: 'Creating Memorable Corporate Events',
    excerpt: 'Learn how to transform corporate gatherings into engaging experiences that leave lasting impressions.',
    content: '',
    image_url: 'https://images.pexels.com/photos/4717526/pexels-photo-4717526.jpeg?auto=compress&cs=tinysrgb&w=800',
    category: 'Corporate Events',
    author_name: 'James Chen',
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    post_id: 'sample3',
    title: 'Budget Planning: Maximizing Your Event Investment',
    excerpt: 'Strategic approaches to event budgeting that ensure maximum impact without compromising on quality.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1750277122806-a0b0277e1328?q=85&w=800&auto=format&fit=crop',
    category: 'Planning Tips',
    author_name: 'Emily Rodriguez',
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
  }
];

const categories = ['All', 'Strategy', 'Planning Tips', 'Corporate Events', 'Design Trends', 'Weddings', 'Inspiration'];

export const Blog = () => {
  const [posts, setPosts] = useState(samplePosts);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        if (response.data.length > 0) {
          setPosts(response.data);
        }
      } catch (error) {
        // Falls back to sample posts already in state
        console.warn('Failed to load blog posts:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === 'All' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen pt-24 bg-[#050505]" data-testid="blog-page">
      <SEO
        title="Event Strategy Insights | White Glove Event Concierge"
        description="Practical guidance on Nashville event budgets, vendor contracts, strategic event concierge services, and high-value event decisions."
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Our Blog</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Insights &{' '}
              <span className="italic text-[#D4AF37]">Inspiration</span>
            </h1>
            <p className="text-white/60 text-lg leading-relaxed">
              Explore our collection of articles on event strategy, planning insights, and industry expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`text-sm uppercase tracking-widest transition-colors ${
                  selectedCategory === category
                    ? 'text-[#D4AF37]'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="blog-posts">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#0a0a0a] border border-white/10 overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-white/5" />
                  <div className="p-6">
                    <div className="h-4 bg-white/10 rounded w-1/4 mb-4" />
                    <div className="h-6 bg-white/10 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-white/10 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-white/60">No posts found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.post_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-[#0a0a0a] border border-white/10 overflow-hidden hover:border-[#D4AF37]/50 transition-colors"
                  data-testid={`blog-post-${index}`}
                >
                  <Link to={`/blog/${post.post_id}`}>
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1767050190883-29d644fa5b99?q=85&w=800&auto=format&fit=crop'}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-[#D4AF37] text-xs uppercase tracking-widest">
                        {post.category}
                      </span>
                      <h3 className="font-heading text-xl text-white mt-2 mb-3 group-hover:text-[#D4AF37] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-white/60 text-sm line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-white/40 text-xs">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5">
                            <User size={12} />
                            {post.author_name}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar size={12} />
                            {format(new Date(post.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                        <ArrowRight size={14} className="group-hover:text-[#D4AF37] transition-colors" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
